/**
 * Order and vehicle projections (datasets `orders` and `order_vehicles`).
 * Orders and vehicles keep distinct identities: an order row shows aggregates,
 * each vehicle row owns its own VIN, stock/lot number and auction details.
 * A VIN suffix is never used as a lot/stock value.
 */

export type OrderStatus = 'offered' | 'dispatched' | 'picked_up' | 'delivered' | 'cancelled';

export interface OrderRaw {
  id: string;
  order_date: string;
  status: OrderStatus;
  revision: number;
  company_id: string;
  /** Reversible local archive mark; null while active. */
  archived_at: string | null;
  company_name: string | null;
  reference: string | null;
  source: string | null;
  /** Pickup stop: actual time, else scheduled window start. Null when unscheduled. */
  pickup_at: string | null;
  delivery_at: string | null;
  pickup_city: string | null;
  pickup_region: string | null;
  delivery_city: string | null;
  delivery_region: string | null;
  vehicle_count: number;
  last_free_date: string | null;
  transport_minor: number | null;
  currency: string | null;
  /** none: no transport charge; mixed: several currencies, so no single amount. */
  transport_state: 'none' | 'single' | 'mixed';
  /** Vehicle×kind evidence slots that are due and absent; null without documents.read. */
  evidence_missing: number | null;
  /** Slots with an unfinished upload; null without documents.read. */
  evidence_unchecked: number | null;
}

/**
 * Explicit per-vehicle document state. 'stale' is reserved: provider freshness of
 * evidence is not modeled yet, so the server never emits it today.
 */
export type DocState = 'ready' | 'missing' | 'not_due' | 'unchecked' | 'restricted' | 'stale' | 'unavailable';
export const EVIDENCE_KINDS = ['pickup_photo', 'bol', 'delivery_photo'] as const;
export type EvidenceKind = typeof EVIDENCE_KINDS[number];

export interface VehicleRaw {
  id: string;
  order_id: string;
  model_year: number | null;
  make: string | null;
  model: string | null;
  vin: string | null;
  stock_number: string | null;
  last_free_date: string | null;
  keys_status: 'yes' | 'no' | 'unknown' | 'unchecked' | null;
  condition_text: string | null;
  detail_observed_at: string | null;
  /** Provider account that produced the auction details (shown only with a connection grant). */
  connection_label: string | null;
  provider_code: string | null;
  revision: number;
  pickup_photo_state: DocState;
  bol_state: DocState;
  delivery_photo_state: DocState;
}

/** Fields the server allows for orders sorting/filtering. */
export const ORDER_SORT_FIELDS = ['pickup_at', 'delivery_at', 'order_date', 'source', 'reference', 'company_name', 'vehicle_count', 'pickup_city', 'delivery_city', 'status', 'evidence_missing', 'last_free_date', 'transport_minor'] as const;
export type OrderSortField = typeof ORDER_SORT_FIELDS[number];

export const ORDER_STATUSES: readonly OrderStatus[] = ['offered', 'dispatched', 'picked_up', 'delivered', 'cancelled'];

/** Mirrors app.set_order_status transitions for enabling controls; the server decides. */
export function allowedTransitions(status: OrderStatus): OrderStatus[] {
  switch (status) {
    case 'offered': return ['dispatched', 'cancelled'];
    case 'dispatched': return ['picked_up', 'cancelled'];
    case 'picked_up': return ['delivered'];
    default: return [];
  }
}

/** Provider codes from app.providers; LCD is visibly distinct from CD. */
export const SOURCE_LABELS: Record<string, { short: string; name: string }> = {
  cd: { short: 'CD', name: 'Central Dispatch' },
  lcd: { short: 'LCD', name: 'Legacy Central Dispatch' },
  sd: { short: 'SD', name: 'Super Dispatch' },
  moov: { short: 'Moov', name: 'Moov Auto' },
  copart: { short: 'Copart', name: 'Copart' },
  iaa: { short: 'IAA', name: 'IAA' },
};

/** Dataset `order_stops` (scope = order ID). */
export interface StopRaw {
  id: string;
  position: number;
  kind: 'pickup' | 'delivery' | string;
  label: string | null;
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  region: string | null;
  postal_code: string | null;
  country_code: string | null;
  window_start: string | null;
  window_end: string | null;
  actual_at: string | null;
}

export type Urgency = 'overdue' | 'today' | 'soon' | null;

/**
 * Last-free-day urgency from a verified provider date, relative to the user's local
 * calendar day. Null date means no verified value; that is not "no urgency known".
 */
export function lastFreeDayUrgency(isoDate: string | null, today: string, status?: OrderStatus): Urgency {
  if (!isoDate) return null;
  // Urgency applies only while the vehicle still awaits collection.
  if (status && status !== 'offered' && status !== 'dispatched') return null;
  if (isoDate < today) return 'overdue';
  if (isoDate === today) return 'today';
  const days = (Date.parse(isoDate) - Date.parse(today)) / 86400000;
  return days <= 2 ? 'soon' : null;
}

/** Last six VIN characters for compact display; the full VIN stays available. */
export function vinTail(vin: string | null): string | null {
  return vin && vin.length >= 6 ? vin.slice(-6) : vin;
}

/** Local calendar date (YYYY-MM-DD) in the given time zone. */
export function localDate(now: Date, timeZone?: string): string {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(now);
  const get = (type: string) => parts.find(p => p.type === type)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}
